<script setup lang="ts">
import { computed } from 'vue'
import type { FolderItem } from '@/modules/documents-new/types/folder.types'
import { ROOT_FOLDER_ID } from '@/modules/documents-new/composables/useFolders'

interface Props {
  folder: FolderItem
  selectedFolderId?: string | null
  level?: number
  selectable?: boolean
  selectOnArrow?: boolean
}

interface Emits {
  (e: 'select', folderId: string | null, folderName: string): void
  (e: 'toggle', folderId: string): void
}

const props = withDefaults(defineProps<Props>(), {
  level: 0,
  selectable: true,
  selectOnArrow: true,
})
const emit = defineEmits<Emits>()
const FOLDER_INDENT_BASE = 14
const FOLDER_INDENT_STEP = 10

const isExpanded = computed(() => props.folder.expanded ?? false)

const hasChildren = computed(() => !!props.folder.children?.length)

const isSelected = computed(() => {
  if (props.folder.id === ROOT_FOLDER_ID) {
    return props.selectedFolderId === null
  }

  if (props.selectedFolderId == null) {
    return false
  }

  return String(props.selectedFolderId) === String(props.folder.id)
})

const headerStyle = computed(() => {
  if (props.level <= 0) return undefined

  return {
    paddingLeft: `${FOLDER_INDENT_BASE + props.level * FOLDER_INDENT_STEP}px`,
  }
})

const handleToggle = () => {
  if (hasChildren.value) emit('toggle', props.folder.id)
}

const handleSelect = () => {
  if (props.selectable) {
    const folderId
      = props.folder.id === ROOT_FOLDER_ID ? null : props.folder.id

    emit('select', folderId, props.folder.name)
  }
}

const handleArrowClick = () => {
  handleToggle()
  if (props.selectOnArrow) {
    handleSelect()
  }
}

const handleHeaderClick = (event: MouseEvent) => {
  const target = event.target
  if (target instanceof Element && target.closest('.folder-icon')) {
    return
  }

  handleSelect()
}
</script>

<template>
  <div class="folder-item">
    <div
      class="folder-header"
      :class="{
        'folder-header--selected': isSelected,
      }"
      :style="headerStyle"
      @click="handleHeaderClick"
    >
      <VIcon
        v-if="hasChildren"
        :icon="isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"
        size="20"
        class="folder-icon"
        @click.stop="handleArrowClick"
      />
      <span v-else class="folder-icon-placeholder" />
      <span class="folder-name">{{ folder.name }}</span>
      <span class="folder-count">{{ folder.count_documents }}</span>
    </div>

    <div
      v-if="isExpanded && hasChildren"
      class="folder-children"
      :class="{ 'folder-children--nested': level > 0 }"
    >
      <FolderTreeItem
        v-for="child in folder.children"
        :key="child.id"
        :folder="child"
        :selected-folder-id="selectedFolderId"
        :level="level + 1"
        :selectable="selectable"
        :select-on-arrow="selectOnArrow"
        @select="(id, name) => emit('select', id, name)"
        @toggle="(id) => emit('toggle', id)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.folder-item {
  user-select: none;
  position: relative;
}

.folder-header {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  transition: color 0.2s;
  gap: 4px;
  cursor: pointer;
  position: relative;

  &--selected {
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: #24abaf;
    }

    .folder-name {
      color: #24abaf;
    }

    .folder-icon {
      color: #24abaf;
    }
  }
}

.folder-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  transition: color 0.2s;
  cursor: pointer;

  &:hover {
    color: #24abaf;
  }
}

.folder-icon-placeholder {
  flex-shrink: 0;
  width: 20px;
  margin-top: 1px;
}

.folder-name {
  flex: 1;
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  transition: color 0.2s;
  cursor: pointer;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.35;

  &:hover {
    color: #24abaf;
  }
}

.folder-count {
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 1px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  padding: 2px 8px;
  background: rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 10px;
  min-width: 28px;
  text-align: center;
  transition:
    color 0.2s,
    background 0.2s;
  font-weight: 500;

  .folder-header:hover &,
  .folder-header--selected & {
    color: #24abaf;
    background: rgba(36, 171, 175, 0.16);
  }
}

.folder-children {
  padding-left: 0;

  &--nested {
    padding-left: 0;
  }
}
</style>
